/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Observable } from 'rxjs';
import { NamedValue } from 'app/shared/models';
import { StaticDataType } from 'app/static-data/models';
import { DataService } from 'app/static-data/services/data.service';
import { map } from 'rxjs/operators';

/** A decorator that allows adjusting what the names associated with returned NamedValue objects should be. */
export class NameOverridingDataService extends DataService {
  /**
   * Allows adjusting what the names associated with specific values should be.
   */
  private readonly nameOverrides: { [key: string]: { [key: string]: string } } = {
    emailType: {
      ALERTS: 'Threshold breach alerts',
      FILE_ATTACHMENT: 'Email with files as attachments',
      PARSING_AND_CALCULATION_STATUS: 'Email with parsing errors and calculation status'
    }
  };

  /**
   * Invokes the method on the delegate, and overrides certain values' descriptions.
   *
   * @param enumType the type of the enum whose values to load
   * @return associated enum values
   */
  loadEnumerationValues(enumType: StaticDataType): Observable<NamedValue<string>[]> {
    return this.delegate
      .loadEnumerationValues(enumType)
      .pipe(map(values => this.applyOverrides(values, enumType)));
  }

  /**
   * Applies overrides for values returned by the delegate.
   *
   * If no overrides are defined for the enum type, the values are unchanged.
   *
   * @param namedValues values returned by the delegate
   * @param enumType the type of the enum
   */
  private applyOverrides(
    namedValues: NamedValue<string>[],
    enumType: StaticDataType
  ): NamedValue<string>[] {
    if (enumType in this.nameOverrides) {
      const overrides = this.nameOverrides[enumType];
      return namedValues.map(namedValue =>
        Object.keys(overrides).includes(namedValue.value)
          ? {
              name: overrides[namedValue.value],
              value: namedValue.value
            }
          : namedValue
      );
    } else {
      return namedValues; // no change
    }
  }

  // -------------------------------------------------------------------------
  constructor(private delegate: DataService) {
    super();
  }
}
